import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI, api } from '../services/api';

// Import logos
import myAssetLogo from '../assets/logos/myasset-logo.svg';
import optiTracLogo from '../assets/logos/optitrac-logo.svg';
import trac365Logo from '../assets/logos/365trac-logo.svg';
import canaryLogo from '../assets/logos/canary-logo.svg';
import inPlayLogo from '../assets/logos/inplay-logo.svg';
import shadowLogo from '../assets/logos/shadow-logo.png';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changePasswordData, setChangePasswordData] = useState<{userId: string; clientId: string; username: string; userType?: string} | null>(null);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      if (isForgotPassword) {
        await authAPI.forgotPassword(email);
        setSuccess('Password reset instructions sent to your email!');
        setIsForgotPassword(false);
        setEmail('');
      } else if (isRegister) {
        await authAPI.register(username, email, password);
        setIsRegister(false);
        setSuccess('Registration successful! Please login.');
        setUsername('');
        setEmail('');
        setPassword('');
      } else {
        // Login attempt
        try {
          const response = await authAPI.login(username, password, otp || undefined);
          
          // Check if OTP is required
          if (response.data.requiresPasswordChange) {
            setChangePasswordData({ userId: response.data.userId, clientId: response.data.clientId, username: response.data.username, userType: response.data.userType });
            setShowChangePassword(true);
            setSuccess('Please set a new password to continue.');
          } else if (response.data.requiresOTP) {
            setShowOTPInput(true);
            setOtpEmail(response.data.email);
            setSuccess('OTP sent to your email. Please check your inbox and enter the code below.');
            setOtpAttempts(0);
          } else {
            // Login successful
            login(response.data.token, response.data.user);
            setSuccess('Login successful!');
          }
        } catch (adminError: any) {
          // If admin login fails, try client login
          if (adminError.response?.status === 401 && !showOTPInput) {
            try {
              const clientResponse = await api.post('/client-registration/login', { username, password });
              
              // Check if password change is required
              if (clientResponse.data.requiresPasswordChange) {
                sessionStorage.setItem('passwordChangeData', JSON.stringify({
                  userId: clientResponse.data.userId,
                  clientId: clientResponse.data.clientId,
                  username: clientResponse.data.username,
                  userType: clientResponse.data.userType
                }));
                navigate('/portal/change-password');
                return;
              }
              
              localStorage.setItem('clientToken', clientResponse.data.token);
              localStorage.setItem('clientUser', JSON.stringify(clientResponse.data.user));
              window.location.href = '/';
            } catch (clientError: any) {
              throw new Error('Invalid username or password');
            }
          } else {
            throw adminError;
          }
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'An error occurred';
      setError(errorMessage);
      
      // If OTP error, increment attempts
      if (showOTPInput && errorMessage.includes('Invalid OTP')) {
        setOtpAttempts(prev => prev + 1);
        if (otpAttempts >= 2) {
          setShowOTPInput(false);
          setOtp('');
          setError('Too many failed attempts. Please try logging in again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/change-password', { userId: changePasswordData!.userId, clientId: changePasswordData!.clientId, newPassword, userType: changePasswordData!.userType });
      setShowChangePassword(false);
      setSuccess('Password updated! Please log in with your new password.');
      setPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setChangePasswordData(null);
    } catch (err: any) {
      setError('Failed to update password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!otpEmail) return;
    
    setLoading(true);
    try {
      await api.post('/auth/resend-otp', { email: otpEmail });
      setSuccess('New OTP sent to your email!');
      setOtp('');
      setOtpAttempts(0);
    } catch (error: any) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setPassword('');
    setEmail('');
    setOtp('');
    setError('');
    setSuccess('');
    setShowOTPInput(false);
    setOtpEmail('');
    setOtpAttempts(0);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: 'url(/assets/login-bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Logo Header */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '20px 0',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-around',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px',
          padding: '0 20px'
        }}>
          <div style={{ height: '60px', display: 'flex', alignItems: 'center' }}>
            <img src={myAssetLogo} alt="MyAsset Logo" style={{ height: '50px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }} />
          </div>
          <div style={{ height: '60px', display: 'flex', alignItems: 'center' }}>
            <img src={optiTracLogo} alt="OptiTrac Logo" style={{ height: '50px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }} />
          </div>
          <div style={{ height: '60px', display: 'flex', alignItems: 'center' }}>
            <img src={trac365Logo} alt="365Trac Logo" style={{ height: '50px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }} />
          </div>
          <div style={{ height: '60px', display: 'flex', alignItems: 'center' }}>
            <img src={canaryLogo} alt="Canary Logo" style={{ height: '50px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }} />
          </div>
          <div style={{ height: '60px', display: 'flex', alignItems: 'center' }}>
            <img src={inPlayLogo} alt="InPlay Logo" style={{ height: '50px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }} />
          </div>
          <div style={{ height: '60px', display: 'flex', alignItems: 'center' }}>
            <img src={shadowLogo} alt="Shadow Logo" style={{ height: '50px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          padding: '0',
          width: '100%',
          maxWidth: '900px',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            minHeight: '500px'
          }}>
            {/* Left Side - Branding */}
            <div style={{
              background: 'linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)',
              padding: '60px 40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              color: 'white',
              position: 'relative'
            }}>
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
                opacity: 0.3
              }}></div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ marginBottom: '32px', textAlign: 'center' }}>
                  <img src="/assets/optitrac-horizontal-logo.png" alt="OptiTrac Logo" 
                       style={{ height: '60px', width: 'auto', maxWidth: '250px', objectFit: 'contain' }} />
                </div>
                <h1 style={{
                  fontSize: '48px', fontWeight: '700', margin: '0 0 16px 0',
                  background: 'linear-gradient(135deg, #fff 0%, #e8e8e8 100%)',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'
                }}>
                  Optitrac Device Management Portal
                </h1>
                <h2 style={{ fontSize: '24px', fontWeight: '300', margin: '0 0 32px 0', opacity: 0.9 }}>
                  Comprehensive Tracking System
                </h2>
                <div style={{
                  width: '60px', height: '4px', background: 'linear-gradient(90deg, #3498db, #2ecc71)',
                  borderRadius: '2px', marginBottom: '32px'
                }}></div>
                <p style={{ fontSize: '16px', lineHeight: '1.6', opacity: 0.8, margin: 0 }}>
                  Manage your clients, devices, and applications with our comprehensive tracking system.
                </p>
              </div>
            </div>

            {/* Right Side - Login Form */}
            <div style={{
              padding: '60px 40px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <div style={{ marginBottom: '40px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div>
                  <img src={optiTracLogo} alt="OptiTrac Logo" 
                       style={{ height: '40px', width: 'auto', maxWidth: '80px', objectFit: 'contain' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '28px', fontWeight: '600', color: '#2c3e50', margin: '0 0 8px 0' }}>
                    {showChangePassword ? 'Set New Password' : isForgotPassword ? 'Reset Password' : (isRegister ? 'Create Account' : 'Welcome Back')}
                  </h3>
                  <p style={{ color: '#7f8c8d', fontSize: '16px', margin: 0 }}>
                    {isForgotPassword ? 'Enter your email to reset password' : 
                     (isRegister ? 'Join our platform today' : 
                      showOTPInput ? 'Enter the verification code sent to your email' : 'Sign in to your account')}
                  </p>
                </div>
              </div>

              {success && (
                <div style={{
                  backgroundColor: '#d4edda', color: '#155724', padding: '12px 16px',
                  borderRadius: '8px', marginBottom: '24px', fontSize: '14px', border: '1px solid #c3e6cb'
                }}>
                  {success}
                </div>
              )}

              {error && (
                <div style={{
                  backgroundColor: '#fee', color: '#c33', padding: '12px 16px',
                  borderRadius: '8px', marginBottom: '24px', fontSize: '14px', border: '1px solid #fcc'
                }}>
                  {error}
                </div>
              )}

              {showChangePassword ? (
                <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔐</div>
                    <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>Welcome <strong>{changePasswordData?.username}</strong>! Please set your new password.</p>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>New Password</label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required minLength={6} placeholder="Enter new password" style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '15px', boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500, color: '#374151' }}>Confirm Password</label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required minLength={6} placeholder="Confirm new password" style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '15px', boxSizing: 'border-box' }} />
                  </div>
                  <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 600, cursor: 'pointer' }}>
                    {loading ? 'Updating...' : 'Set New Password'}
                  </button>
                </form>
              ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {!isForgotPassword && !showOTPInput && (
                  <div>
                    <label style={{
                      display: 'block', marginBottom: '8px', fontWeight: '500',
                      color: '#2c3e50', fontSize: '14px'
                    }}>Username</label>
                    <input
                      type="text" value={username} onChange={(e) => setUsername(e.target.value)} required
                      style={{
                        width: '100%', padding: '14px 16px', border: '2px solid #ecf0f1',
                        borderRadius: '8px', fontSize: '16px', transition: 'border-color 0.3s', outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3498db'}
                      onBlur={(e) => e.target.style.borderColor = '#ecf0f1'}
                    />
                  </div>
                )}

                {(isRegister || isForgotPassword) && (
                  <div>
                    <label style={{
                      display: 'block', marginBottom: '8px', fontWeight: '500',
                      color: '#2c3e50', fontSize: '14px'
                    }}>Email</label>
                    <input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      style={{
                        width: '100%', padding: '14px 16px', border: '2px solid #ecf0f1',
                        borderRadius: '8px', fontSize: '16px', transition: 'border-color 0.3s', outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3498db'}
                      onBlur={(e) => e.target.style.borderColor = '#ecf0f1'}
                    />
                  </div>
                )}

                {!isForgotPassword && !showOTPInput && (
                  <div>
                    <label style={{
                      display: 'block', marginBottom: '8px', fontWeight: '500',
                      color: '#2c3e50', fontSize: '14px'
                    }}>Password</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password} onChange={(e) => setPassword(e.target.value)} required
                        style={{
                          width: '100%', padding: '14px 50px 14px 16px', border: '2px solid #ecf0f1',
                          borderRadius: '8px', fontSize: '16px', transition: 'border-color 0.3s', outline: 'none'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#3498db'}
                        onBlur={(e) => e.target.style.borderColor = '#ecf0f1'}
                      />
                      <button
                        type="button" onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#7f8c8d', fontSize: '18px', padding: '4px'
                        }}
                      >
                        {showPassword ? '👁️' : '👁️🗨️'}
                      </button>
                    </div>
                  </div>
                )}

                {showOTPInput && (
                  <div>
                    <label style={{
                      display: 'block', marginBottom: '8px', fontWeight: '500',
                      color: '#2c3e50', fontSize: '14px'
                    }}>
                      Verification Code
                    </label>
                    <input
                      type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code" maxLength={6} required
                      style={{
                        width: '100%', padding: '14px 16px', border: '2px solid #ecf0f1',
                        borderRadius: '8px', fontSize: '18px', textAlign: 'center',
                        letterSpacing: '4px', fontFamily: 'monospace',
                        transition: 'border-color 0.3s', outline: 'none'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#3498db'}
                      onBlur={(e) => e.target.style.borderColor = '#ecf0f1'}
                    />
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                      <button
                        type="button" onClick={handleResendOTP} disabled={loading}
                        style={{
                          background: 'none', border: 'none', color: '#3498db',
                          fontSize: '14px', cursor: 'pointer', textDecoration: 'underline'
                        }}
                      >
                        Resend Code
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit" disabled={loading}
                  style={{
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    color: 'white', border: 'none', padding: '16px', borderRadius: '8px',
                    fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 4px 12px rgba(29, 78, 216, 0.3)',
                    opacity: loading ? 0.7 : 1, marginBottom: '10px'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(29, 78, 216, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(29, 78, 216, 0.3)';
                  }}
                >
                  {loading ? 'Please wait...' : 
                   (isForgotPassword ? 'Send Reset Email' : 
                    (isRegister ? 'Create Account' : 
                     (showOTPInput ? 'Verify Code' : 'Sign In')))}
                </button>
              </form>
              )}

              {!showChangePassword && (
              <div style={{
                textAlign: 'center', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #ecf0f1'
              }}>
                {showOTPInput ? (
                  <button
                    onClick={() => {
                      setShowOTPInput(false);
                      setOtp('');
                      setError('');
                      setSuccess('');
                    }}
                    style={{
                      background: 'none', border: 'none', color: '#6c757d',
                      fontSize: '14px', cursor: 'pointer', textDecoration: 'underline'
                    }}
                  >
                    Back to login
                  </button>
                ) : !isForgotPassword ? (
                  <>
                    <button
                      onClick={() => {
                        setIsRegister(!isRegister);
                        resetForm();
                      }}
                      style={{
                        background: 'none', border: 'none', color: '#3498db',
                        fontSize: '14px', cursor: 'pointer', textDecoration: 'underline',
                        marginBottom: '8px', display: 'block', width: '100%'
                      }}
                    >
                      {isRegister ? 'Already have an account? Sign in' : 'Need an account? Register'}
                    </button>
                    <button
                      onClick={() => {
                        setIsForgotPassword(true);
                        resetForm();
                      }}
                      style={{
                        background: 'none', border: 'none', color: '#6c757d',
                        fontSize: '14px', cursor: 'pointer', textDecoration: 'underline'
                      }}
                    >
                      Forgot your password?
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setIsForgotPassword(false);
                      resetForm();
                    }}
                    style={{
                      background: 'none', border: 'none', color: '#3498db',
                      fontSize: '14px', cursor: 'pointer', textDecoration: 'underline'
                    }}
                  >
                    Back to login
                  </button>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
